import { useContext, useEffect, useState } from 'react';
import { IconButton } from '@upmc/ims.component-library';
import Typography from '@mui/material/Typography';
import Icon from '../../../../../components/UI/MDC/Icon/Icon';
import {
	AppsMenuIcon,
	RequestMenuIcon,
	SettingsMenuIcon,
	StaffMenuIcon,
	SupportMenuIcon,
} from '../../../../../components/Navigation/Icons/MenuIcons';
import { getMenuFavorites } from '../../../../../components/Navigation/_api';
import styles from './FavoritesWidget.module.scss';
import IMSLink from '../../../../../components/Navigation/Links/IMSLink';
import { UserContext } from 'context/User/UserContext';
import { Widget, WidgetTypes } from '../../Widget/Widget';
import CustomizeFavorites from '../../../../../components/Navigation/CustomizeFavorites/CustomizeFavorites';
import classNames from 'classnames';
import WithLoadingHandler from '../../../../../hoc/withLoadingHandler/withLoadingHandler';

const FavoritesWidget = () => {
	const [groupedFavorites, setGroupedFavorites] = useState([]);
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user = contextUser.userInfo.impersonationProfile ?? contextUser.userInfo.userProfile;
	const [showFavorites, setShowFavorites] = useState(false);
	const iconsMap = {
		request: <RequestMenuIcon />,
		staff: <StaffMenuIcon />,
		apps: <AppsMenuIcon />,
		support: <SupportMenuIcon />,
		settings: <SettingsMenuIcon />,
	};
	const [isLoading, setIsLoading] = useState(true);

	const fillFavorites = (favoritesArray, upperLimit) => {
		let filledFavorites = [...(favoritesArray || [])];

		while (filledFavorites.length < upperLimit) {
			filledFavorites.push({ Name: '--None--' });
		}
		const groupedFavorites = groupByKey(filledFavorites);
		setGroupedFavorites(groupedFavorites);

		return filledFavorites;
	};

	const groupByKey = arr => {
		const result = arr.reduce((r, a) => {
			r[a.TopLevelParentName] = r[a.TopLevelParentName] || [];
			r[a.TopLevelParentName].push(a);
			return r;
		}, Object.create(null));

		return result;
	};

	useEffect(() => {
		if (user?.ID) {
			getMenuFavorites(user.ID)
				.then(fav => {
					fillFavorites(fav.MenuItems, fav.MenuItemsUpperLimit);
				})
				.then(result => {
					setIsLoading(false);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	const refreshFavorites = () => {
		setIsLoading(true);
		getMenuFavorites(user?.ID)
			.then(fav => {
				fillFavorites(fav.MenuItems, fav.MenuItemsUpperLimit);
			})
			.then(result => {
				setIsLoading(false);
			});
	};

	const CustomActions = () => {
		return (
			<IconButton
				onClick={() => setShowFavorites(true)}
				id='favoritesWidgetEdit'
			>
				<Icon icon='edit' />
			</IconButton>
		);
	};

	const getIcon = header => {
		if (header.toLowerCase() in iconsMap) {
			return iconsMap[header.toLowerCase()];
		}
		return <AppsMenuIcon />;
	};
	return (
		<Widget
			title='Favorites'
			subtitle='Your favorite pages.'
			className={styles.cardContainer}
			customActions={CustomActions}
			onRefresh={refreshFavorites}
			type={WidgetTypes.accordionOpen}
		>
			<WithLoadingHandler isLoading={isLoading}>
				{showFavorites ? (
					<CustomizeFavorites
						onClickHideFavorites={() => setShowFavorites(false)}
						onFavoritesUpdate={() => refreshFavorites()}
					/>
				) : null}
				<div className={styles.group_container}>
					{Object.entries(groupedFavorites)
						.filter(fav => fav[0] !== 'undefined')
						.map((group, index) => (
							<div
								className={classNames([styles.group])}
								key={index}
							>
								<div className={styles.groupHeader}>
									{getIcon(group[0])}
									<Typography>{group[0]}</Typography>
								</div>

								<div className={styles.cardInnerContainer}>
									{group[1]?.map((favItems, index) => (
										<IMSLink
											url={favItems.ArgumentPage}
											key={'link_' + index}
											id={`favLink_${group[0]}_${index}`}
										>
											<div
												className={styles.card}
												key={
													`favItem_${index}` +
													favItems.Name
												}
											>
												<Typography
													variant='body2'
													align='center'
													fontWeight={500}
												>
													{favItems.Name}
												</Typography>
											</div>
										</IMSLink>
									))}
								</div>
							</div>
						))}
				</div>
			</WithLoadingHandler>
		</Widget>
	);
};

export default FavoritesWidget;
